import * as XLSX from 'xlsx';
import type { LegacyDashboardClient } from '../models/LegacyDashboardClient';

export type LegacyDashboardTranslate = (key: string) => string;

function formatBool(value: boolean | null | undefined, t: LegacyDashboardTranslate): string {
  if (value === null || value === undefined) return '';
  return value ? t('table.yes') : t('table.no');
}

function formatPaymentBreakdown(breakdown: LegacyDashboardClient['payment_breakdown']): string {
  if (!breakdown) return '';
  const parts: string[] = [];
  if (breakdown.cod_count !== undefined && breakdown.cod_percentage !== undefined) {
    parts.push(`COD: ${breakdown.cod_count} (${breakdown.cod_percentage}%)`);
  }
  if (breakdown.card_count !== undefined && breakdown.card_percentage !== undefined) {
    parts.push(`Tarjeta: ${breakdown.card_count} (${breakdown.card_percentage}%)`);
  }
  return parts.join(' · ');
}

function formatDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function buildLegacyDashboardExportRows(
  clients: LegacyDashboardClient[],
  t: LegacyDashboardTranslate,
): Array<Record<string, string | number>> {
  return clients.map((client) => ({
    [t('table.legacy_team_id')]: client.legacy_team_id,
    [t('table.name')]: client.name,
    [t('table.orders_count')]: client.orders_count,
    [t('table.avg_orders_per_month')]: client.avg_orders_per_month,
    [t('table.is_migrated')]: formatBool(client.is_migrated, t),
    [t('table.new_child_team')]: client.new_child_team_name ?? client.new_child_team_id ?? '',
    [t('table.migration_run_status')]: client.migration_run_status
      ? t(`migration_status.${client.migration_run_status}`)
      : '',
    [t('table.shop_channels')]: (client.shop_channels ?? []).join(', '),
    [t('table.payment_profile')]: t(`payment_profile.${client.payment_profile}`),
    [t('table.payment_breakdown')]: formatPaymentBreakdown(client.payment_breakdown),
    [t('table.admin_name')]: client.admin?.name ?? '',
    [t('table.admin_email')]: client.admin?.email ?? '',
    [t('table.admin_phone')]: client.admin?.phone ?? '',
    [t('table.country_code')]: client.country_code ?? '',
    [t('table.verified')]: formatBool(client.verified, t),
    [t('table.is_dropshipping')]: formatBool(client.is_dropshipping, t),
    [t('table.crm_active')]: formatBool(client.crm_active, t),
    [t('table.sga_products_count')]: client.sga?.products_count ?? '',
    [t('table.sga_products_with_stock_count')]: client.sga?.products_with_stock_count ?? '',
    [t('table.sga_total_stock')]: client.sga?.total_stock ?? '',
    [t('table.sga_total_stock_available')]: client.sga?.total_stock_available ?? '',
    [t('table.sga_total_stock_reserved')]: client.sga?.total_stock_reserved ?? '',
  }));
}

export function exportLegacyDashboardToExcel(
  clients: LegacyDashboardClient[],
  t: LegacyDashboardTranslate,
): { blob: Blob; filename: string } {
  if (clients.length === 0) {
    throw new Error('NO_DATA');
  }

  const rows = buildLegacyDashboardExportRows(clients, t);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, t('export.sheet_name').slice(0, 31));

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  return {
    blob: new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename: `clientes-legacy-activos-${formatDateLocal(new Date())}.xlsx`,
  };
}

export function downloadLegacyDashboardExcel(clients: LegacyDashboardClient[], t: LegacyDashboardTranslate): void {
  const { blob, filename } = exportLegacyDashboardToExcel(clients, t);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
